import React, {FC} from 'react';
import classNames from 'classnames';
import ArrowIcon from 'components/ArrowIcon';
import {Markdown} from 'components/Markdown';
import {useToggle} from 'hooks/useToggle';
import {DexaMetric, Section, SectionColor} from 'templates/reports/types';

import Citation from './Citation';
import {ColorSplit} from './ColorSplit';

import * as styles from './styles.module.scss';

interface SectionProps {
	children: React.ReactNode
	color?: SectionColor
	colorSplit?: boolean
	details: Section['details']
	separator?: boolean
	open?: boolean | 'always'
	metric?: DexaMetric
	anchor?: string
}

const _Section: FC<SectionProps> = ({
	children,
	details,
	color = 'white',
	colorSplit,
	separator = true,
	open: startOpen = false,
	metric,
	anchor,
}) => {
	const alwaysOpen = startOpen === 'always';
	const {open, toggle} = useToggle(alwaysOpen || startOpen);
	const OpenComponent = colorSplit ? ColorSplit : React.Fragment;
	return (
		<div id={metric || anchor} className={classNames({
			[styles.wrapper]: true,
			[styles.white]: color === 'white',
			[styles.green]: color === 'green',
			[styles.blue]: color === 'blue',
		})}>
			<div className={styles.always}>
				<div className={styles.header} onClick={alwaysOpen ? undefined : toggle}>
					<div className={styles.title}>
						{details.header.title}
					</div>
					{!alwaysOpen && (
						<ArrowIcon className={styles.arrow} open={open} />
					)}
				</div>
				{(details.header.summary || details.citations) && (
					<div className={classNames({
						[styles.summary]: true,
						[styles.hide]: open,
					})}>
						<Markdown markdown={details.header.summary} />
						<Citation
							citations={details.citations}
							parentOpen={open}
							openParent={toggle}
						/>
					</div>
				)}
			</div>
			{open && (
				<OpenComponent>
					{separator && !colorSplit && <div className={styles.separator} />}
					<div className={styles.content}>
						{children}
					</div>
					{!alwaysOpen && (
						<div className={styles.collapse}>
							<span onClick={toggle}>Collapse section</span>
						</div>
					)}
				</OpenComponent>
			)}
		</div>
	);
};

export default _Section;
